// {feed}/events.json (§14.5). Eventos validados um a um; tipo desconhecido é ignorado, nunca recusa o arquivo (I-19).
// P-27: `insight_state` avisa a mudança de estado do cartão de um lugar (a entrada e o desfecho, no máximo dois por
// lugar por noite), com o nível REGION e a natureza `analysis`; é um tipo que nunca vai a toast, overlay, modo TV,
// ticker nem LiveRegion (DR-33).
import * as z from 'zod/mini';
import {
  type CandidateNumber,
  CandidateNumberSchema,
  type FeedHeader,
  feedHeaderShape,
  type IbgeCode,
  IbgeCodeSchema,
  IntSchema,
  type Iso,
  IsoSchema,
  type Office,
  OfficeSchema,
  type PlaceCode,
  PlaceCodeSchema,
  type TseCode,
  TseCodeSchema,
  type UfCode,
  UfCodeSchema,
} from './base';
// Do núcleo, e não de insights.ts: este arquivo está no caminho crítico do site ao vivo (B01, §15.7).
import {
  type InsightPlace,
  InsightPlaceSchema,
  type InsightState,
  InsightStateSchema,
  insightPlaceKey,
} from './insights.core';
import { type ChanceBand, ChanceBandSchema } from './probability';

export type ElectionEventType =
  | 'results_open'
  | 'abroad_first_votes'
  | 'abroad_half'
  | 'abroad_complete'
  | 'uf_first_votes'
  | 'uf_complete'
  | 'capital_complete'
  | 'sections_batch'
  | 'count_milestone'
  | 'national_lead_change'
  | 'uf_lead_change'
  | 'math_decision'
  | 'tse_result'
  | 'model_activated'
  | 'model_band_change'
  | 'model_paused'
  | 'model_resumed'
  | 'model_ended'
  | 'tse_stalled'
  | 'tse_resumed'
  | 'correction'
  | 'insight_state';

export const ELECTION_EVENT_TYPES = [
  'results_open',
  'abroad_first_votes',
  'abroad_half',
  'abroad_complete',
  'uf_first_votes',
  'uf_complete',
  'capital_complete',
  'sections_batch',
  'count_milestone',
  'national_lead_change',
  'uf_lead_change',
  'math_decision',
  'tse_result',
  'model_activated',
  'model_band_change',
  'model_paused',
  'model_resumed',
  'model_ended',
  'tse_stalled',
  'tse_resumed',
  'correction',
  'insight_state',
] as const satisfies readonly ElectionEventType[];

/**
 * Tipos que nunca vão a toast, overlay, modo TV, ticker nem LiveRegion (DR-33): o cartão de insight muda de estado no
 * painel e ganha um pino na linha do tempo, sem notificação. Quem distribui eventos para essas superfícies filtra por
 * esta lista.
 */
export const NON_BROADCAST_EVENT_TYPES = ['insight_state'] as const satisfies readonly ElectionEventType[];

/** Onde o fato aconteceu. Com REGION (P-27), o RegionCode: o nível distingue a UF 'SE' da região 'SE'. */
export interface ElectionEventScope {
  level: 'BR' | 'REGION' | 'UF' | 'MUN' | 'ABROAD';
  /** UF, IBGE, RegionCode ou 'ZZ'; ausente em BR. */
  code?: string;
}

export type ElectionEventData =
  | { type: 'results_open' }
  | { type: 'abroad_first_votes'; cities: TseCode[] }
  | { type: 'uf_first_votes'; places: UfCode[] }
  | {
      type: 'sections_batch';
      sections: number;
      places: PlaceCode[];
      shares: [number, number];
      national_sections_fraction: number;
    }
  | {
      type: 'count_milestone';
      milestone: 0.01 | 0.05 | 0.1 | 0.25 | 0.5 | 0.75 | 0.9 | 0.99;
      shares: [number, number];
    }
  | {
      type: 'national_lead_change' | 'uf_lead_change';
      from: CandidateNumber;
      to: CandidateNumber;
      sections_fraction: number;
      crossing?: { sections_fraction: number; share_to: number };
    }
  | {
      type: 'abroad_half' | 'abroad_complete' | 'uf_complete' | 'capital_complete';
      leader: CandidateNumber | null;
      shares: [number, number];
      margin_pp: number;
      ibge?: IbgeCode;
    }
  | { type: 'math_decision' | 'tse_result'; winner: CandidateNumber }
  | { type: 'model_activated' | 'model_band_change'; favorite: CandidateNumber | null; band: ChanceBand }
  | { type: 'model_paused' | 'model_resumed' | 'model_ended'; reason: string | null }
  | { type: 'tse_stalled' | 'tse_resumed'; since: Iso }
  | {
      type: 'correction';
      field: 'sections_counted' | 'valid_votes' | 'votes';
      candidate?: CandidateNumber;
      before: number;
      after: number;
    }
  /**
   * A mudança de estado do cartão de um lugar (P-27, G-9): a entrada (`from` null) e o desfecho, no máximo dois por
   * lugar por noite. Saliência 1 ou 2, nunca 3, e a do desfecho é a da entrada: o desfecho tem o mesmo destaque
   * (DR-33). Só lugares de nível BR, REGION, UF, MUN ou ABROAD_CITY geram evento; zona, local e seção ficam no índice.
   */
  | {
      type: 'insight_state';
      place: InsightPlace;
      /** Estado do cartão antes; null = entrada. */
      from: InsightState | null;
      /** Estado do cartão depois da transição. */
      to: InsightState;
      /** Ids dos sinais (Insight.id) que mudaram com o cartão; nunca vazio. */
      signals: string[];
    };

export interface ElectionEvent {
  /**
   * `${type}:${scope.code ?? 'BR'}:${seq}` — determinístico (§14.5). No `insight_state`, a chave do lugar entra no
   * lugar do código do escopo (`insight_state:${insightPlaceKey(place)}:${seq}`): 'SE' é UF e região, e todas as
   * cidades do Exterior têm o escopo 'ZZ'.
   */
  id: string;
  type: ElectionEventType;
  office: Office;
  /** seq do snapshot em que o fato ocorreu (§14.8.8). */
  seq: number;
  /** tse_ht do fato (ou interpolação). */
  occurred_at: Iso;
  /** published_at do snapshot que confirmou. */
  detected_at: Iso;
  /** No `insight_state`, o escopo é o do lugar (insightStateScope). */
  scope: ElectionEventScope;
  salience: 1 | 2 | 3;
  /**
   * 'analysis' (P-27) = comparação estatística com o esperado: nem dado oficial, nem estimativa do modelo de chance. No
   * acervo (P-26, item 7), também o `math_decision` da noite reconstruída: cálculo do Mapa Voto sobre os quadros.
   */
  nature: 'official' | 'model' | 'ops' | 'analysis';
  data: ElectionEventData;
  /** Ignorado pela interface (§13.5). */
  text?: { line: string; short?: string; screen_reader?: string; ticker?: string };
}

export interface EventList extends FeedHeader<'results.events'> {
  /** occurred_at decrescente. No feed ao vivo, os 300 mais recentes; no acervo, a noite inteira (P-26, item 7). */
  events: ElectionEvent[];
  truncated: boolean;
}

const shares = z.tuple([z.number(), z.number()]);

export const ElectionEventDataSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('results_open') }),
  z.object({ type: z.literal('abroad_first_votes'), cities: z.array(TseCodeSchema) }),
  z.object({ type: z.literal('uf_first_votes'), places: z.array(UfCodeSchema) }),
  z.object({
    type: z.literal('sections_batch'),
    sections: IntSchema,
    places: z.array(PlaceCodeSchema),
    shares,
    national_sections_fraction: z.number(),
  }),
  z.object({
    type: z.literal('count_milestone'),
    milestone: z.union([
      z.literal(0.01),
      z.literal(0.05),
      z.literal(0.1),
      z.literal(0.25),
      z.literal(0.5),
      z.literal(0.75),
      z.literal(0.9),
      z.literal(0.99),
    ]),
    shares,
  }),
  z.object({
    type: z.enum(['national_lead_change', 'uf_lead_change']),
    from: CandidateNumberSchema,
    to: CandidateNumberSchema,
    sections_fraction: z.number(),
    crossing: z.optional(z.object({ sections_fraction: z.number(), share_to: z.number() })),
  }),
  z.object({
    type: z.enum(['abroad_half', 'abroad_complete', 'uf_complete', 'capital_complete']),
    leader: z.nullable(CandidateNumberSchema),
    shares,
    margin_pp: z.number(),
    ibge: z.optional(IbgeCodeSchema),
  }),
  z.object({ type: z.enum(['math_decision', 'tse_result']), winner: CandidateNumberSchema }),
  z.object({
    type: z.enum(['model_activated', 'model_band_change']),
    favorite: z.nullable(CandidateNumberSchema),
    band: ChanceBandSchema,
  }),
  z.object({ type: z.enum(['model_paused', 'model_resumed', 'model_ended']), reason: z.nullable(z.string()) }),
  z.object({ type: z.enum(['tse_stalled', 'tse_resumed']), since: IsoSchema }),
  z.object({
    type: z.literal('correction'),
    field: z.enum(['sections_counted', 'valid_votes', 'votes']),
    candidate: z.optional(CandidateNumberSchema),
    before: IntSchema,
    after: IntSchema,
  }),
  z.object({
    type: z.literal('insight_state'),
    place: InsightPlaceSchema,
    from: z.nullable(InsightStateSchema),
    to: InsightStateSchema,
    // Insight.id de cada sinal: 32 hex minúsculos (o mesmo padrão de InsightIdSchema, que mora fora do caminho crítico)
    signals: z.array(z.string().check(z.regex(/^[0-9a-f]{32}$/))),
  }),
]);

export const ElectionEventSchema = z.object({
  id: z.string(),
  type: z.enum(ELECTION_EVENT_TYPES),
  office: OfficeSchema,
  seq: IntSchema,
  occurred_at: IsoSchema,
  detected_at: IsoSchema,
  scope: z.object({ level: z.enum(['BR', 'REGION', 'UF', 'MUN', 'ABROAD']), code: z.optional(z.string()) }),
  salience: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  nature: z.enum(['official', 'model', 'ops', 'analysis']),
  data: ElectionEventDataSchema,
  text: z.optional(
    z.object({
      line: z.string(),
      short: z.optional(z.string()),
      screen_reader: z.optional(z.string()),
      ticker: z.optional(z.string()),
    }),
  ),
});

/** Eventos um a um (I-19): o arquivo só exige a lista. */
export const EventListSchema = z.object({
  ...feedHeaderShape('results.events'),
  events: z.array(z.unknown()),
  truncated: z.boolean(),
});
export const EventListPublishedSchema = z.object({
  ...feedHeaderShape('results.events'),
  events: z.array(ElectionEventSchema),
  truncated: z.boolean(),
});

/** Id determinístico (§14.5). No `insight_state`, passe a chave do lugar (insightPlaceKey) no lugar do código do escopo. */
export function eventId(type: string, scopeCode: string | undefined, seq: number): string {
  return `${type}:${scopeCode ?? 'BR'}:${seq}`;
}

/** Id do `insight_state` de um lugar num seq: `insight_state:mun-3550308:288`. */
export function insightStateEventId(place: InsightPlace, seq: number): string {
  return eventId('insight_state', insightPlaceKey(place), seq);
}

/**
 * Escopo do `insight_state` (I-26): o do lugar. Município com o IBGE; cidade do Exterior com ABROAD e 'ZZ'; BR, REGION
 * e UF iguais ao lugar. null = lugar que não gera evento (zona, local de votação e seção).
 */
export function insightStateScope({ level, code }: InsightPlace): ElectionEventScope | null {
  if (level === 'ABROAD_CITY') return { level: 'ABROAD', code: 'ZZ' };
  return level === 'BR' || level === 'REGION' || level === 'UF' || level === 'MUN' ? { level, code } : null;
}
